import type { components } from "@brandex/api-client";
import { formatTaka, translate, type MessageKey } from "@brandex/i18n";
import { useBrandex } from "@brandex/dashboard";
import { Badge, Button, Input, Spinner, cn } from "@brandex/ui";
import { BarChart3, PencilLine, UserPlus } from "lucide-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { QueryError } from "../views";
import { can, useStaffAccess } from "../access";

type ClientPage = components["schemas"]["ClientPageDto"];
type ClientRow = components["schemas"]["ClientRowDto"];
type Status = components["schemas"]["ClientStatusDto"];
type Tone = "neutral" | "success" | "warning" | "accent";

const TABS: { status: Status | undefined; label: string; count: keyof ClientPage["counts"] }[] = [
  { status: undefined, label: "All", count: "all" },
  { status: "active", label: "Active", count: "active" },
  { status: "onboarding", label: "Onboarding", count: "onboarding" },
  { status: "paused", label: "Paused", count: "paused" },
  { status: "inactive", label: "Inactive", count: "inactive" },
];

const STATUS: Record<Status, { label: string; tone: Tone }> = {
  active: { label: "Active", tone: "success" },
  onboarding: { label: "Onboarding", tone: "warning" },
  paused: { label: "Paused", tone: "neutral" },
  inactive: { label: "Inactive", tone: "neutral" },
};

const SITE: Record<ClientRow["website_state"], { label: string; tone: Tone }> = {
  live: { label: "Live", tone: "success" },
  awaiting_activation: { label: "Awaiting activation", tone: "warning" },
  awaiting_verification: { label: "Awaiting BMDC check", tone: "warning" },
  unpublished: { label: "Unpublished by doctor", tone: "neutral" },
  offline: { label: "Offline", tone: "warning" },
  not_included: { label: "No website", tone: "neutral" },
  needs_onboarding: { label: "Not onboarded", tone: "neutral" },
};

const PAYMENT: Record<NonNullable<ClientRow["payment"]>, { label: string; tone: Tone }> = {
  paid: { label: "Paid", tone: "success" },
  due: { label: "Due", tone: "accent" },
  overdue: { label: "Overdue", tone: "warning" },
  not_billed: { label: "Not billed", tone: "neutral" },
};

/** Debounces a fast-changing value (search box). */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Admin clients (F-17): every doctor with package, portfolio, this month's
 * delivery and payment status. Filters and search run on the server; prices
 * and payment status show only to staff who handle payments.
 */
export function ClientsPage() {
  const { api, nav } = useBrandex();
  const access = useStaffAccess();
  // Opening accounts needs `platform.users.manage`.
  const canOnboard = can(access.data, "platform.users.manage");
  // Maintaining a client's practice needs `platform.clients.manage`.
  const canManage = can(access.data, "platform.clients.manage", { assigned: true });
  const [status, setStatus] = useState<Status | undefined>(undefined);
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim(), 250);
  const list = useInfiniteQuery({
    queryKey: ["admin", "clients", status ?? "all", q],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data } = await api.http.GET("/v1/admin/clients", {
        params: {
          query: {
            limit: 25,
            ...(status === undefined ? {} : { status }),
            ...(q === "" ? {} : { search: q }),
            ...(pageParam === undefined ? {} : { cursor: pageParam }),
          },
        },
      });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const first = list.data?.pages[0];
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Clients</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every doctor on BrandEx. Delivery is this month{first ? ` (${first.period})` : ""}: approved or published out of planned.
          </p>
        </div>
        {canOnboard ? (
          <Button onClick={() => nav.navigate("/clients/new")}>
            <UserPlus className="h-4 w-4" aria-hidden />
            Onboard client
          </Button>
        ) : null}
      </header>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" aria-label="Client status" className="flex flex-wrap gap-1 rounded-[var(--radius-pill)] border border-border p-1">
          {TABS.map((tab) => (
            <button
              key={tab.label}
              type="button"
              role="tab"
              aria-selected={status === tab.status}
              onClick={() => setStatus(tab.status)}
              className={cn(
                "rounded-full px-3 py-1 text-sm text-muted-foreground hover:text-foreground",
                status === tab.status && "bg-primary text-primary-foreground hover:text-primary-foreground",
              )}
            >
              {tab.label}
              {first ? <> <span className="tabular-nums opacity-80">{first.counts[tab.count]}</span></> : null}
            </button>
          ))}
        </div>
        <Input aria-label="Search clients" placeholder="Search doctor or clinic" className="h-9 w-64" value={search} maxLength={100} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {list.isPending ? <Spinner label="Loading…" /> : null}
      {list.isError ? <QueryError error={list.error} /> : null}
      {list.data !== undefined ? (
        <div className="glass overflow-x-auto rounded-[var(--radius-lg)]">
          <table className="w-full min-w-[980px] text-sm">
            <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Doctor</th>
                <th>Package</th>
                <th>Portfolio</th>
                <th>Delivery</th>
                <th>Payment</th>
                <th>Status</th>
                <th className="px-4">Joined</th>
                <th className="px-4"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No clients match.</td></tr>
              ) : (
                rows.map((r) => (
                  <Row
                    key={r.organization_id}
                    r={r}
                    onReport={() => nav.navigate(`/clients/${r.organization_id}/report`)}
                    onManage={canManage ? () => nav.navigate(`/clients/${r.organization_id}/manage`) : undefined}
                  />
                ))
              )}
            </tbody>
          </table>
          {list.hasNextPage ? (
            <div className="p-3"><Button size="sm" variant="outline" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>Load more</Button></div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Row({ r, onReport, onManage }: { r: ClientRow; onReport: () => void; onManage?: (() => void) | undefined }) {
  const pct = r.delivery.total === 0 ? 0 : Math.round((r.delivery.done / r.delivery.total) * 100);
  const pkg = r.package;
  return (
    <tr className="align-top">
      <td className="px-4 py-3">
        <p className="font-medium">{r.doctor_name ?? r.organization_name}</p>
        <p className="text-xs text-muted-foreground">
          {r.specialty ? translate("en", `specialty.${r.specialty}` as MessageKey) : "Not onboarded yet"}
          {r.doctor_name && r.doctor_name !== r.organization_name ? ` · ${r.organization_name}` : ""}
        </p>
      </td>
      <td className="py-3">
        {pkg ? (
          <>
            <p>{pkg.kind === "custom" ? "Custom" : (pkg.plan_name?.en ?? "Standard")} <span className="text-xs text-muted-foreground">· {pkg.status}</span></p>
            <p className="text-xs text-muted-foreground">
              {pkg.monthly_price_paisa !== null && pkg.monthly_price_paisa !== undefined ? `${formatTaka("en", pkg.monthly_price_paisa)} / month` : pkg.kind === "custom" && pkg.status !== "active" ? "To quote" : ""}
              {pkg.includes_website ? " · website" : ""}
            </p>
          </>
        ) : (
          <span className="text-muted-foreground">No package</span>
        )}
      </td>
      <td className="py-3">
        <Badge tone={SITE[r.website_state].tone}>{SITE[r.website_state].label}</Badge>
        {r.website_slug ? <p className="mt-1 text-xs text-muted-foreground"><code>/d/{r.website_slug}</code></p> : null}
      </td>
      <td className="py-3 pr-4">
        {r.delivery.total === 0 ? (
          <span className="text-muted-foreground">Nothing planned</span>
        ) : (
          <div className="w-32">
            <p className="text-xs tabular-nums">{r.delivery.done} / {r.delivery.total}</p>
            <div role="progressbar" aria-label={`Delivery for ${r.doctor_name ?? r.organization_name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-mint-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </td>
      <td className="py-3">
        {r.payment ? <Badge tone={PAYMENT[r.payment].tone}>{PAYMENT[r.payment].label}</Badge> : <span className="text-xs text-muted-foreground">Payments staff only</span>}
      </td>
      <td className="py-3"><Badge tone={STATUS[r.status].tone}>{STATUS[r.status].label}</Badge></td>
      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{new Date(r.joined_at).toLocaleDateString("en-GB", { dateStyle: "medium" })}</td>
      <td className="px-4 py-3">
        <div className="flex gap-1">
          {r.specialty && onManage ? (
            <Button size="sm" variant="ghost" aria-label={`Manage ${r.doctor_name ?? r.organization_name}`} onClick={onManage}>
              <PencilLine className="h-4 w-4" aria-hidden />
              Manage
            </Button>
          ) : null}
          {r.doctor_name ? (
            <Button size="sm" variant="ghost" aria-label={`Report for ${r.doctor_name}`} onClick={onReport}>
              <BarChart3 className="h-4 w-4" aria-hidden />
              Report
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
